import React, { useState, useRef, useEffect } from 'react';
import axios from "axios";
import { Box, TextField, Button, Typography, FormControlLabel, Checkbox, Grid, CircularProgress, useTheme, FormControl, InputLabel, Select, MenuItem, Switch, Chip, OutlinedInput, ListItemText } from "@mui/material";
import { CloudUpload, Check, Clear, Link, LinkOff } from "@mui/icons-material";
import SignatureCanvas from 'react-signature-canvas';
import { base_url } from '../../config/config';
import { getToken } from "../../config/config";
import { tokens } from "../../theme";

const ProcessImprovementForm = ({ onProcessCreated, colors: propColors, initialData }) => {
  const theme = useTheme();
  const colors = propColors || tokens(theme.palette.mode);
  
  const sigCanvas = useRef({});
  const fileInputRef = useRef(null);
  
  // Form state - moved before useEffect
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    description: initialData?.description || '',
    image: initialData?.image || null,
    signature: '',
    lawsAcknowledgement: false,
    isConnectedToReklamation: initialData?.relatedReklamationId ? true : false,
    relatedReklamationId: initialData?.relatedReklamationId || null,
    visibleToEmployees: [], // New field for employee visibility
    signatureRequired: false, // New field to require signatures
  });
  
  // Fetch available reklamations on component mount
  useEffect(() => {
    if (formData.isConnectedToReklamation) {
      fetchAvailableReklamations();
    }
  }, [formData.isConnectedToReklamation]);

  // Fetch available employees on component mount
  useEffect(() => {
    fetchAvailableEmployees();
  }, []);
  
  // UI states
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [signatureComplete, setSignatureComplete] = useState(false);
  
  // Reklamation states
  const [availableReklamations, setAvailableReklamations] = useState([]);
  const [loadingReklamations, setLoadingReklamations] = useState(false);
  
  // Employee states
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  
  // Fetch available reklamations
  const fetchAvailableReklamations = async () => {
    setLoadingReklamations(true);
    try {
      const response = await axios.get(
        `${base_url}/v1/getAllReklamations`,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );
      
      if (response.data && response.data.length > 0) {
        setAvailableReklamations(response.data);
      }
    } catch (error) {
      console.error('Error fetching reklamations:', error);
      setErrors({
        ...errors,
        reklamation: 'Fehler beim Laden der Reklamationen'
      });
    } finally {
      setLoadingReklamations(false);
    }
  };

  // Fetch available employees
  const fetchAvailableEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const response = await axios.get(
        `${base_url}/v1/getAllEmployees`,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );
      
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setAvailableEmployees(response.data.data);
      } else if (response.data && Array.isArray(response.data)) {
        setAvailableEmployees(response.data);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      setErrors(prev => ({
        ...prev,
        employees: 'Fehler beim Laden der Mitarbeiter'
      }));
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'isConnectedToReklamation') {
      setFormData({
        ...formData,
        [name]: checked,
        relatedReklamationId: checked ? formData.relatedReklamationId : null,
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value,
      });
    }
    
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  // Handle employee selection change
  const handleEmployeeSelectionChange = (event) => {
    const { value } = event.target;
    setFormData({
      ...formData,
      visibleToEmployees: typeof value === 'string' ? value.split(',') : value,
    });
  };
  
  // Handle image upload
  const handleImageUploadToS3 = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file is an image
    if (!file.type.match('image.*')) {
      setErrors({
        ...errors,
        image: 'File must be an image',
      });
      return;
    }
    
    // Create local preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(file);
    
    // Upload to S3 via backend
    setIsUploading(true);
    
    try {
      const formDataForUpload = new FormData();
      formDataForUpload.append('file', file);
      
      const response = await axios.post(
        `${base_url}/v1/uploadFile`,
        formDataForUpload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${getToken()}`
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      // Set the S3 URL to the form data
      if (response.data && response.data.fileUrl) {
        setFormData({
          ...formData,
          image: response.data.fileUrl
        });
      }
      
    } catch (error) {
      console.error('Error uploading file:', error);
      setErrors({
        ...errors,
        image: 'Bild konnte nicht hochgeladen werden. Bitte versuchen Sie es erneut.'
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };
  
  // Handle signature actions
  const handleClearSignature = () => {
    sigCanvas.current.clear();
    setSignatureComplete(false);
    setFormData({
      ...formData,
      signature: ''
    });
  };
  
  const handleCompleteSignature = () => {
    if (sigCanvas.current.isEmpty()) {
      setErrors({
        ...errors,
        signature: 'Signature is required'
      });
      return;
    }
    
    // Convert signature to data URL
    const signatureDataUrl = sigCanvas.current.toDataURL('image/png');
    
    setFormData({
      ...formData,
      signature: signatureDataUrl
    });
    
    setSignatureComplete(true);
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Titel ist erforderlich';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Beschreibung ist erforderlich';
    }
    
    if (!formData.signature) {
      newErrors.signature = 'Unterschrift ist erforderlich';
    }
    
    if (!formData.lawsAcknowledgement) {
      newErrors.lawsAcknowledgement = 'Sie müssen die Bedingungen bestätigen';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(
        `${base_url}/v1/createProcessImprovement`,
        formData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getToken()}`
          }
        }
      );
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        image: null,
        signature: '',
        lawsAcknowledgement: false,
        isConnectedToReklamation: false,
        relatedReklamationId: null,
        visibleToEmployees: [],
        signatureRequired: false,
      });
      setImagePreview('');
      handleClearSignature();
      
      // Notify parent component
      if (onProcessCreated && response.data.data) {
        onProcessCreated(response.data.data);
      }
      
    } catch (error) {
      console.error('Error creating process improvement:', error);
      setErrors({
        ...errors,
        form: error.response?.data?.message || 'Failed to create process improvement. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Typography 
        variant="h5" 
        color={colors.greenAccent[400]} 
        fontWeight="bold" 
        mb={3}
      >
        Create New Process Improvement
      </Typography>
      
      {errors.form && (
        <Box 
          p={2} 
          mb={3} 
          bgcolor={colors.redAccent[500]} 
          color={colors.grey[100]}
          borderRadius="4px"
        >
          <Typography variant="body2">{errors.form}</Typography>
        </Box>
      )}
      
      <Grid container spacing={3}>
        {/* Title */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Titel"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            error={Boolean(errors.title)}
            helperText={errors.title}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: colors.grey[400],
                },
                "&:hover fieldset": {
                  borderColor: colors.greenAccent[400],
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.greenAccent[400],
                },
              },
            }}
          />
        </Grid>
        
        {/* Description */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={5}
            label="Beschreibung"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            error={Boolean(errors.description)}
            helperText={errors.description}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: colors.grey[400],
                },
                "&:hover fieldset": {
                  borderColor: colors.greenAccent[400],
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.greenAccent[400],
                },
              },
            }}
          />
        </Grid>
        
        {/* Reklamation Connection */}
        <Grid item xs={12}>
          <Box 
            p={3} 
            bgcolor={colors.primary[400]} 
            borderRadius="8px"
            border={`1px solid ${colors.grey[400]}`}
          >
            <Typography variant="h6" color={colors.greenAccent[400]} gutterBottom>
              Reklamation Verknüpfung
            </Typography>
            <Typography variant="body2" color={colors.grey[300]} sx={{ mb: 2 }}>
              Wählen Sie, ob diese Prozessverbesserung mit einer bestehenden Reklamation verknüpft werden soll
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isConnectedToReklamation}
                  onChange={handleChange}
                  name="isConnectedToReklamation"
                  color="primary"
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  {formData.isConnectedToReklamation ? <Link /> : <LinkOff />}
                  <Typography color={colors.grey[100]}>
                    {formData.isConnectedToReklamation 
                      ? 'Mit Reklamation verknüpft' 
                      : 'Eigenständige Prozessverbesserung'
                    }
                  </Typography>
                </Box>
              }
            />
            
            {formData.isConnectedToReklamation && (
              <Box mt={2}>
                <FormControl fullWidth>
                  <InputLabel>Reklamation auswählen</InputLabel>
                  <Select
                    value={formData.relatedReklamationId || ''}
                    onChange={(e) => setFormData({ ...formData, relatedReklamationId: e.target.value })}
                    label="Reklamation auswählen"
                    disabled={loadingReklamations}
                  >
                    <MenuItem value="">
                      <em>Keine Reklamation auswählen</em>
                    </MenuItem>
                    {availableReklamations.map((reklamation) => (
                      <MenuItem key={reklamation._id} value={reklamation._id}>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {reklamation.anr || 'Kein ANR'} - {reklamation.referenceOrderNumber || 'Keine Referenz'}
                          </Typography>
                          <Typography variant="caption" color={colors.grey[400]}>
                            {reklamation.description?.substring(0, 60)}...
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {formData.relatedReklamationId && (
                  <Box mt={1}>
                    <Chip 
                      icon={<Link />}
                      label="Reklamation verknüpft"
                      color="primary"
                      size="small"
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Grid>
        
        {/* Employee Visibility Selection */}
        <Grid item xs={12}>
          <Box 
            p={3} 
            bgcolor={colors.primary[400]} 
            borderRadius="8px"
            border={`1px solid ${colors.grey[400]}`}
          >
            <Typography variant="h6" color={colors.greenAccent[400]} gutterBottom>
              Mitarbeiter-Sichtbarkeit & Unterschriften
            </Typography>
            <Typography variant="body2" color={colors.grey[300]} sx={{ mb: 2 }}>
              Wählen Sie die Mitarbeiter aus, die diese Prozessverbesserung sehen können
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Sichtbar für Mitarbeiter</InputLabel>
              <Select
                multiple
                value={formData.visibleToEmployees}
                onChange={handleEmployeeSelectionChange}
                input={<OutlinedInput label="Sichtbar für Mitarbeiter" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((employeeId) => {
                      const employee = availableEmployees.find(emp => emp._id === employeeId);
                      return (
                        <Chip 
                          key={employeeId} 
                          label={employee ? `${employee.name || employee.fullName || employee.email}` : employeeId}
                          size="small" 
                        />
                      );
                    })}
                  </Box>
                )}
                disabled={loadingEmployees}
              >
                <MenuItem value="">
                  <em>Alle Mitarbeiter (Standardeinstellung)</em>
                </MenuItem>
                {availableEmployees.map((employee) => (
                  <MenuItem key={employee._id} value={employee._id}>
                    <Checkbox checked={formData.visibleToEmployees.indexOf(employee._id) > -1} />
                    <ListItemText 
                      primary={employee.name || employee.fullName || employee.email} 
                      secondary={employee.email || employee.position}
                    />
                  </MenuItem>
                ))}
              </Select>
              {loadingEmployees && (
                <Typography variant="caption" color={colors.grey[400]} sx={{ mt: 1 }}>
                  Mitarbeiter werden geladen...
                </Typography>
              )}
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.signatureRequired}
                  onChange={handleChange}
                  name="signatureRequired"
                  color="primary"
                />
              }
              label={
                <Typography color={colors.grey[100]}>
                  Unterschriften von ausgewählten Mitarbeitern erforderlich
                </Typography>
              }
            />
            
            {formData.signatureRequired && formData.visibleToEmployees.length > 0 && (
              <Box mt={2}>
                <Typography variant="body2" color={colors.grey[300]}>
                  Die folgenden Mitarbeiter müssen diese Prozessverbesserung unterschreiben:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {formData.visibleToEmployees.map((employeeId) => {
                    const employee = availableEmployees.find(emp => emp._id === employeeId);
                    return (
                      <Chip 
                        key={employeeId}
                        label={employee ? `${employee.name || employee.fullName || employee.email}` : employeeId}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
        
        {/* Image Upload */}
        <Grid item xs={12}>
          <Typography 
            variant="subtitle2" 
            mb={1}
          >
            Prozessbild
          </Typography>
          
          <Box 
            p={3} 
            bgcolor={colors.grey[800]} 
            borderRadius="4px"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            border={`1px dashed ${colors.grey[400]}`}
            sx={{ cursor: 'pointer' }}
            onClick={() => fileInputRef.current.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageUploadToS3}
            />
            
            {imagePreview ? (
              <Box width="100%" position="relative">
                <img 
                  src={imagePreview} 
                  alt="Process Preview" 
                  style={{ 
                    width: '100%', 
                    maxHeight: '200px', 
                    objectFit: 'contain',
                    borderRadius: '4px',
                  }} 
                />
                
                {isUploading && (
                  <Box 
                    position="absolute" 
                    top={0} 
                    left={0} 
                    width="100%" 
                    height="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bgcolor="rgba(0, 0, 0, 0.5)"
                    borderRadius="4px"
                  >
                    <Box textAlign="center">
                      <CircularProgress 
                        variant="determinate" 
                        value={uploadProgress} 
                        sx={{ color: colors.greenAccent[400] }}
                      />
                      <Typography 
                        variant="body2" 
                        color={colors.grey[100]}
                        mt={1}
                      >
                        {uploadProgress}% Hochgeladen
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <>
                <CloudUpload fontSize="large" sx={{ color: colors.grey[400], mb: 1 }} />
                <Typography color={colors.grey[300]}>
                  Klicken Sie, um ein Bild hochzuladen
                </Typography>
                <Typography variant="body2" color={colors.grey[400]} mt={1}>
                  PNG, JPG bis zu 5MB
                </Typography>
              </>
            )}
          </Box>
          
          {errors.image && (
            <Typography variant="body2" color={colors.redAccent[500]} mt={1}>
              {errors.image}
            </Typography>
          )}
        </Grid>
        
        {/* Signature Canvas */}
        <Grid item xs={12}>
          <Typography 
            variant="subtitle2" 
            mb={1}
          >
            Unterschrift*
          </Typography>
          
          <Box 
            bgcolor={colors.grey[800]}
            borderRadius="4px"
            border={errors.signature 
              ? `1px solid ${colors.redAccent[500]}` 
              : `1px solid ${colors.grey[400]}`
            }
            p={1}
          >
            <SignatureCanvas
              ref={sigCanvas}
              penColor={colors.grey[100]}
              canvasProps={{
                className: 'signature-canvas',
                style: {
                  width: '100%',
                  height: '100%',
                },
              }}
              disabled={signatureComplete}
            />
          </Box>
          
          <Box 
            mt={2} 
            display="flex" 
            justifyContent="flex-end"
            gap={2}
          >
            <Button
              variant="outlined"
              startIcon={<Clear />}
              onClick={handleClearSignature}
              sx={{
                borderColor: colors.redAccent[400],
                color: colors.redAccent[400],
                '&:hover': {
                  borderColor: colors.redAccent[300],
                  backgroundColor: 'rgba(228, 106, 118, 0.08)',
                }
              }}
            >
              Löschen
            </Button>
            
            <Button
              variant="contained"
              startIcon={<Check />}
              onClick={handleCompleteSignature}
              disabled={signatureComplete}
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[900],
                '&:hover': {
                  backgroundColor: colors.greenAccent[500],
                },
                '&.Mui-disabled': {
                  backgroundColor: colors.greenAccent[800],
                  color: colors.grey[500],
                }
              }}
            >
              {signatureComplete ? 'Unterschrieben' : 'Unterschreiben'}
            </Button>
          </Box>
          
          {errors.signature && (
            <Typography variant="body2" color={colors.redAccent[500]} mt={1}>
              {errors.signature}
            </Typography>
          )}
        </Grid>
        
        {/* LAWS Acknowledgment */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox 
                checked={formData.lawsAcknowledgement}
                onChange={handleChange}
                name="lawsAcknowledgement"
                sx={{
                  color: colors.greenAccent[400],
                  '&.Mui-checked': {
                    color: colors.greenAccent[400],
                  },
                }}
              />
            }
            label={
              <Typography color={errors.lawsAcknowledgement ? colors.redAccent[500] : colors.grey[100]}>
                Ich bestätige und erkläre mich damit einverstanden, alle relevanten Rechts- und Arbeitssicherheitsstandards einzuhalten*
              </Typography>
            }
          />
          
          {errors.lawsAcknowledgement && (
            <Typography variant="body2" color={colors.redAccent[500]} mt={1}>
              {errors.lawsAcknowledgement}
            </Typography>
          )}
        </Grid>
        
        {/* Submit Button */}
        <Grid item xs={12}>
          <Box 
            display="flex" 
            justifyContent="flex-end"
            mt={2}
          >
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[900],
                fontSize: "14px",
                fontWeight: "bold",
                padding: "10px 20px",
                '&:hover': {
                  backgroundColor: colors.greenAccent[500],
                },
                '&.Mui-disabled': {
                  backgroundColor: colors.grey[700],
                  color: colors.grey[500],
                }
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} sx={{ color: colors.grey[100] }} />
              ) : 'PROZESSVERBESSERUNG EINREICHEN'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProcessImprovementForm;